import React, { useEffect, useState } from 'react';

import { Box } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { useField } from 'formik';
import { Autocomplete, AutocompleteProps } from 'formik-mui';
import { useSnackbar } from 'notistack';
import { useDebounce } from 'use-debounce';

import { CissTextField } from 'components/fields/';
import { RequestListagemProps, RequestOptionsType, useRequestListagem } from 'hooks/useRequestListagem';

interface categoryOptionsProps {
    idPecaMarketingFormato: number;
    dsPecaMarketingFormato: string;
}

interface FormatoFieldProps extends AutocompleteProps<categoryOptionsProps, boolean | undefined, boolean | undefined, boolean | undefined> {
    label: string;
}

interface RequestListagemType extends RequestListagemProps {
    data: {
        data: categoryOptionsProps[];
    };
}

export function MarketingFormatoPecaFieldFormik({ onChange, multiple = false, label = 'Formato', ...PropsFormik }: FormatoFieldProps) {
    const [open, setOpen] = useState<boolean>(false);
    const [inputValue, setInputValue] = useState<string>('');
    const [query, setQuery] = useState('');
    const { enqueueSnackbar } = useSnackbar();
    const RequestListagem = useRequestListagem();
    const [field, { error }] = useField(PropsFormik.field.name);
    const [queryValue] = useDebounce(query, 500);

    const requestOptions: RequestOptionsType = {
        url: '/gestao/pecamarketingformato',
        query: queryValue,
        sort: [{ property: 'idPecaMarketingFormato', direction: 'ASC' }],
        columns: 'idPecaMarketingFormato,dsPecaMarketingFormato',
        limit: 50,
    };

    const { isLoading, fetchStatus, data } = useQuery(
        [requestOptions],
        () => {
            const request: Promise<RequestListagemType> = RequestListagem(requestOptions);
            return request;
        },
        {
            keepPreviousData: false,
            enabled: open,
            onError: () => {
                enqueueSnackbar('Falha ao processar solicitação de formato', { variant: 'error' });
            },
        },
    );

    useEffect(() => {
        if (open) {
            setQuery(inputValue);
        }
    }, [inputValue, open]);

    return (
        <Autocomplete
            {...PropsFormik}
            multiple={multiple}
            onChange={onChange}
            loading={isLoading && fetchStatus === 'fetching'}
            options={data?.data?.data ?? []}
            isOptionEqualToValue={(option, value) => option.idPecaMarketingFormato === value.idPecaMarketingFormato}
            getOptionLabel={(option: categoryOptionsProps | string) => {
                if (typeof option === 'string') {
                    return option;
                }

                return option.dsPecaMarketingFormato;
            }}
            renderInput={(params) => <CissTextField label={label} error={Boolean(error)} helperText={error} {...params} />}
            onInputChange={(event, newInputValue) => {
                setInputValue(newInputValue);
            }}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            renderOption={(props, option) => (
                <Box component="li" {...props} key={option.idPecaMarketingFormato}>
                    {option.dsPecaMarketingFormato}
                </Box>
            )}
        />
    );
}
